import type { STEPBody } from 'loaders/step/STEPBody';
import { chain } from 'obliterator';
import { useState, useCallback, useRef, useEffect, forwardRef } from 'react';
import type { ForwardedRef, MutableRefObject } from 'react';
import type { AnyAction } from 'redux';
import styled from 'styled-components';
import { Vector3 } from 'three';

import { createAbortController } from '@frontend/controllers';
import type { CompositeProgressController } from '@frontend/controllers';
import type { MultiMap } from '@frontend/datatypes';

import type { ViewPort } from '../ViewPort';
import {
  ENTITY_TYPES,
  ENVIRONMENT_TYPES,
  STOCK_TYPES,
  UNITS,
  PART_VIEWER_MODES,
} from '../constants';
import type { FORCE_VIEW_TYPES, PART_TYPES } from '../constants';
import type {
  ColorNumber,
  ColorHexString,
  ColorRGBA,
} from '../contracts/colors';
import type { Assembly } from '../loaders/assembly';
import type { AssemblyInstance } from '../loaders/assemblyInstance';
import type { AnyBody, Body } from '../loaders/body';
import type { AnyPart } from '../loaders/part';
import type { StepEdgeModel } from '../loaders/step/StepEdgeModel';
import type { StepFaceModel } from '../loaders/step/StepFaceModel';
import type { StepVertexBody } from '../loaders/step/StepVertexBody';
import { type FetchViewerData, viewerDataService } from '../viewerDataService';
import type { AnyCaliperObject } from './caliper-utils';
import type { FeatureOrFeedBackItem } from './creation-utils';
import type {
  IntersectionTest,
  AnyIntersectionTest,
} from './intersection-utils';
import type { BodyPose, LiteObject, LitState } from './load-utils';
import type { MeasurementObject } from './measurement-utils';
import Renderer from './renderer';
import type { RendererState } from './renderer';
import type { UpdateSelectedObjectsPayload } from './selection-utils';
import type { SnappableFeature } from './snapto-utils';
import type { RawResultForCreation, ResultForCreation } from './stock-utils';
import { loadSheetStockVisualization } from './stock-utils';

const BACKGROUND_MODE_MAP = {
  [PART_VIEWER_MODES.VECTOR_EDIT]: '#fff',
  [PART_VIEWER_MODES.FULL]: 'linear-gradient(160deg, #fff 30%, #888 100%)',
  [PART_VIEWER_MODES.LITE]: 'linear-gradient(160deg, #fff 30%, #888 100%)',
};

export const enum VECTOR_EDITOR_MODES {
  SCALE = 'SCALE',
  LAYERS = 'LAYERS',
}

export type Highlight = {
  type: ENTITY_TYPES;
  body: string;
  indices: number[];
  color?: ColorNumber | ColorHexString | ColorRGBA | null;
  opacity?: number;
  visibility?: boolean;
  linewidth?: number | null;
};

export type UpdateVectorOperation = {
  name: string;
  visible?: boolean;
  toLayer?: string;
  fromLayer?: string;
};

export type Selection = {
  selectedObject: LiteObject;
  viewerState?: LitState;
};

// This is the object type of defined DEFAULT, DELETED, and EXPORT layers in vectorEditor constants
// Used for handling paint-utils coloring at the layer level
// Layer.key === Style.layer
// Layer.label is the displayed text name  of the Layer
export type Layer = {
  key: string;
  label: string;
  color: number;
};

export type MetaData =
  | (
      | StepVertexBody
      | StepEdgeModel
      | StepFaceModel
      | Body<PART_TYPES.STL>
    )['metaData']
  | Record<string, never>
  | undefined;

export type ForceView = {
  payload: { viewerId: string; intersection: AnyIntersectionTest };
  type: FORCE_VIEW_TYPES;
} | null;

const RendererContainer = styled.div<Pick<RenderComponentProps, 'viewerMode'>>`
  background: ${(props: { viewerMode: PART_VIEWER_MODES }) =>
    BACKGROUND_MODE_MAP[props.viewerMode]};
  cursor: pointer;
  display: block;
  height: 100%;
  overflow-y: hidden;
  position: relative;
  width: 100%;
  z-index: 1;
`;

const RendererDiv = forwardRef(
  (
    props: Pick<RenderComponentProps, 'viewerMode'>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <RendererContainer
        //data-fs-hide appears to be for fullstory: TODO: should this be data-fs-exclude?
        //https://help.fullstory.com/hc/en-us/articles/360044349073-FullStory-Private-by-Default#01FHE22KSQ95224Q8T75VQ6X15
        data-fs-hide="true"
        viewerMode={props.viewerMode}
        ref={ref}
      />
    );
  },
);
RendererDiv.displayName = 'RendererDiv';

export type RenderComponentProps = {
  acknowledgeSnapTo: (forcedViewerPose: string) => void;
  activeBodies: string[];
  activeCaliperTool?: boolean;
  activeSelectorTool?: boolean;
  assemblyInstances: AssemblyInstance[];
  bodies: AnyBody[];
  bodyIdsByPartFileUuid: MultiMap<string, string>;
  bodyOrAssemblyInstanceById: Map<string, AnyBody | AssemblyInstance>;
  bodyPoseInformation: BodyPose;
  caliperObjects:
    | []
    | [AnyCaliperObject]
    | [AnyCaliperObject, AnyCaliperObject]; //0, 1, or 2 items
  caliperObjectsKeys: LiteObject[];
  caliperToolSelections: LiteObject[];
  cameraPosition: Vector3;
  cameraUp: Vector3;
  cameraZoom: number;
  closePopover: () => void;
  controlTarget: Vector3;
  dimensionalPrecision: number;
  displayScale: number;
  displayUnits: UNITS;
  editorMode: VECTOR_EDITOR_MODES;
  enableSelectorTool?: () => AnyAction;
  env: ENVIRONMENT_TYPES;
  explodedState: Record<string, number>; //record of assemblyInstanceId to explode scale factor relative to the bounding box's midpoint
  featureSnapTo: SnappableFeature;
  fetchViewerData: FetchViewerData;
  focusedBodies: string[];
  forceFocus?: boolean;
  forceUpdateAdditionalFeedback?: boolean;
  forceView: ForceView;
  forcedViewerPose?: string;
  geometricAnalysisData: {
    // setups?: { referenceUuid: string }[]; //Not used
    // interrogationFiles: unknown //Not used
    features?: FeatureOrFeedBackItem[];
    feedbackItems?: FeatureOrFeedBackItem[];
  };
  hasLoaded?: boolean;
  highlight?: Highlight[];
  historicalCaliperObjects: [AnyCaliperObject, AnyCaliperObject];
  historicalSelectedObjects: AnyCaliperObject[];
  initializeVectorStyleByNameMap?: (obj: STEPBody) => void;
  internalHighlight?: Highlight[];
  isDxfFinalizing?: boolean;
  isFocused?: boolean; //The mouse is focused on something
  isFocusedViewActive?: boolean;
  isPMIViewActive?: boolean;
  isPaused?: boolean;
  isTweening?: boolean;
  loadingProgress: CompositeProgressController;
  materialStockResults: RawResultForCreation<STOCK_TYPES>[];
  maxBoundingBoxDiagonal: number;
  measurement: MeasurementObject | null;
  nativeUnits: UNITS;
  nodeHeight: number;
  nodeWidth: number;
  normals: { point: Vector3; normal: Vector3 }[]; //set by handleObjectClickForCaliperTool() / onObjectClickForCaliperTool()
  onBodyPoseChange: (bodyPose: BodyPose) => Promise<void>;
  onBoxDeselect: (options: { selections: AnyIntersectionTest[] }) => void;
  onBoxSelect: (options: { selections: AnyIntersectionTest[] }) => void;
  onBoxSelectClear: () => void;
  onBoxSelectMove: (options: { pageX: number; pageY: number }) => void;
  onBoxSelectStart: (options: { pageX: number; pageY: number }) => void;
  onCameraPositionChange: (cameraPosition: Vector3) => void;
  onCameraUpChange: (cameraUp: Vector3) => void;
  onCameraZoomChange: (zoom: number) => Promise<void>;
  onControlTargetChange: (target: Vector3) => void;
  onFocusChange: (isFocused: boolean) => void;
  onHasLoaded: (options: { hasLoaded?: boolean }) => void;
  onLoadPartFile: (options: { partFileUuid: string }) => void;
  onMaxBoundingBoxDiagonalChange: (newDiagonal: number) => void;
  onNestingCreationFinish: (options: {
    result: ResultForCreation<STOCK_TYPES>;
  }) => void;
  onNewMetaData: (options: { metaData: MetaData }) => void;
  onNewSelectorCumulativeInfo: (options: {
    cumulativeInfo: { area: number; length: number };
  }) => void;
  onNodeChange: (nodeWidth: number, nodeHeight: number) => void;
  onObjectClickForCaliperTool: <T extends PART_TYPES>(
    options: IntersectionTest<T>,
  ) => void;
  onObjectClickForSelectorTool: <T extends PART_TYPES>(
    options: IntersectionTest<T>,
  ) => void;
  onObjectHover: (options: AnyIntersectionTest[]) => void;
  onOrthoViewChange: (viewPort: ViewPort) => void;
  onRightClick: (options: {
    intersectionTest: AnyIntersectionTest;
    pageX: number;
    pageY: number;
  }) => void;
  //onSetMeasurement() updates measurement property by setting new measurement or null, but also calls onNewMeasurement() and onNewMeasurementComparison() provided to partviewer (if new measurement)
  onSetMeasurement: (measurement: MeasurementObject | null) => void;
  onSimplified: ({
    temporarilySimplified,
    temporarilySimplifiedParts,
    tooComplex,
  }: {
    temporarilySimplified: Set<string>;
    temporarilySimplifiedParts: Set<string>;
    tooComplex: Set<string>;
  }) => void;
  onUpdateSelectionEntities: (options: UpdateSelectedObjectsPayload) => void;
  onlyShowHighlighted?: boolean;
  orthoView: ViewPort;
  partOrAssemblyById: Map<string, AnyPart | Assembly>;
  pendingPartFileUuids: string[];
  pmiUrlByPartFileUuid: Record<string, string>;
  receiveHasPMIAnnotations?: (options: { hasPMIAnnotations: boolean }) => void;
  receiveSerializedDXF?: (options: { dxf: Blob }) => void;
  renderNativeColors?: boolean;
  selectedBodies: string[];
  selectedObjects: AnyCaliperObject[];
  selectedObjectsKeys: LiteObject[];
  selectorToolSelections: { selectedObject: LiteObject }[];
  setReadyForInteraction?: (options: { isReady: boolean }) => void;
  showAdditionalKeys: string[];
  showAdditionalKeysExplicit: string[];
  showAxisAlignedBoundingBox: boolean; //passed as property to viewer-component from frontend app
  showHoverObject?: boolean;
  showOptimalBoundingBox?: boolean;
  showSelectedObjects?: boolean;
  signal: AbortSignal;
  stockCreationFailed: (options: { resultKey: string }) => void;
  stockCreationSuccess: (options: { resultKey: string }) => void;
  temporarilySimplified: Set<string>; //body ids that are temporarily simplified
  temporarilySimplifiedParts: Set<string>; //part ids for bodies that are temporarily simplified
  tooComplex: Set<string>; //part ids that are too complex to render (for any body)
  units: UNITS;
  updatedVectors: UpdateVectorOperation[];
  updatedVectorsCounter: () => AnyAction;
  useCache?: boolean;
  viewerMode: PART_VIEWER_MODES;
  visibleBodies: string[];
  visibleStockResultKeys: string[];
  wireframe?: boolean;
  zoom: number;
};

const RendererComponent = forwardRef(function RendererComponent(
  props: RenderComponentProps,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _ref,
) {
  const {
    // showHoverObject,
    activeBodies,
    activeCaliperTool,
    activeSelectorTool,
    assemblyInstances,
    bodies,
    bodyIdsByPartFileUuid,
    bodyOrAssemblyInstanceById,
    caliperObjectsKeys,
    caliperToolSelections,
    cameraPosition,
    cameraZoom,
    controlTarget,
    dimensionalPrecision,
    displayScale,
    displayUnits,
    editorMode,
    enableSelectorTool,
    explodedState,
    featureSnapTo,
    focusedBodies,
    forceFocus,
    forceView,
    geometricAnalysisData,
    highlight,
    historicalSelectedObjects,
    initializeVectorStyleByNameMap,
    internalHighlight,
    isDxfFinalizing,
    isFocused,
    isFocusedViewActive,
    isPMIViewActive,
    isPaused,
    isTweening,
    loadingProgress,
    materialStockResults,
    measurement,
    nativeUnits,
    normals,
    onNewSelectorCumulativeInfo,
    onlyShowHighlighted,
    orthoView,
    partOrAssemblyById,
    pendingPartFileUuids: _pendingPartFileUuids,
    pmiUrlByPartFileUuid,
    receiveHasPMIAnnotations,
    receiveSerializedDXF,
    renderNativeColors,
    selectedBodies,
    selectedObjects,
    selectedObjectsKeys,
    selectorToolSelections,
    showAdditionalKeys,
    showAdditionalKeysExplicit,
    showAxisAlignedBoundingBox,
    showOptimalBoundingBox,
    updatedVectors,
    updatedVectorsCounter,
    visibleBodies,
    visibleStockResultKeys,
    wireframe,
  } = props;

  // TODO:
  // @William: Can you add something like pendingFeatures, pendingFeedbackItems, onFeatureLoad() and onFeedbackItemLoad()?
  // * I'd like to have mechanism to garbage collect features and feedbackItems when they are loaded
  // * Also: We don't use interrogationFiles or setups
  const { features, feedbackItems } = geometricAnalysisData || {};

  const [targetNode, setTargetNode] = useState<HTMLDivElement | null>(null);

  //Track Sets of pendingPartFileUuids because props.pendingPartFileUuids changes
  //as onLoadPartFile() is called.
  const pendingPartFileUuidsRef = useRef<Set<string>>() as MutableRefObject<
    Set<string>
  >;
  if (!pendingPartFileUuidsRef.current) {
    pendingPartFileUuidsRef.current = new Set();
  }

  const partFileUuidsWithPMIRef = useRef<Set<string>>() as MutableRefObject<
    Set<string>
  >;
  if (!partFileUuidsWithPMIRef.current) {
    partFileUuidsWithPMIRef.current = new Set();
  }

  const abortControllerRef = useRef<AbortController>();
  if (!abortControllerRef.current) {
    abortControllerRef.current = createAbortController();
  }

  const rendererRef = useRef<Renderer | null>(null);
  const rendererStateRef =
    useRef<RendererState>() as MutableRefObject<RendererState>;
  if (!rendererStateRef.current) {
    rendererStateRef.current = {
      signal: abortControllerRef.current.signal,
      forceUpdateAdditionalFeedback: false,
      updatePartPMIVisibility: (state: RendererState) =>
        rendererRef.current?.updatePartPMIVisibility(state),
    } as RendererState; //it will be assigned additional props below
  }
  Object.assign(rendererStateRef.current, props); //IMPORTANT: Every render requires updating rendererState with latest props

  //As soon as targetNode is defined, create renderer
  useEffect(() => {
    if (targetNode && (!rendererRef.current || rendererRef.current.disposed)) {
      const _abortController =
        abortControllerRef.current && !abortControllerRef.current.signal.aborted
          ? abortControllerRef.current
          : createAbortController();
      rendererStateRef.current.signal = _abortController.signal;
      abortControllerRef.current = _abortController;
      rendererRef.current = new Renderer(targetNode, rendererStateRef.current);
    }
  }, [targetNode]);

  useEffect(() => {
    rendererRef.current?.loadFeaturesAndFeedbackItems(
      true,
      chain(features || [], feedbackItems || []),
    );
  }, [features, feedbackItems]);
  useEffect(() => {
    rendererRef.current?.loadPMI(pmiUrlByPartFileUuid);
  }, [pmiUrlByPartFileUuid]);
  useEffect(() => {
    rendererRef.current?.updatePartPMIVisibility(rendererStateRef.current);
  }, [isPMIViewActive]);

  // Load sheet stock visualization for nesting of sheetmetal part
  // Note: rod and bar are handled in renderer.js with handleUpdateCreateMaterialStock()
  useEffect(() => {
    if (
      !bodies ||
      !assemblyInstances ||
      !bodyOrAssemblyInstanceById ||
      !bodyIdsByPartFileUuid ||
      !rendererRef.current
    ) {
      return;
    }
    const resultsToBuild = materialStockResults.filter(
      ({ key, stockType }) =>
        !partOrAssemblyById.has(key) && stockType === STOCK_TYPES.SHEET,
    );
    if (resultsToBuild.length === 1) {
      const result =
        resultsToBuild[0] as RawResultForCreation<STOCK_TYPES.SHEET>;
      loadSheetStockVisualization(
        result,
        rendererRef.current,
        visibleStockResultKeys,
      );
    }
  }, [materialStockResults, partOrAssemblyById, visibleStockResultKeys, bodies, assemblyInstances, bodyIdsByPartFileUuid, bodyOrAssemblyInstanceById]);

  // mimic the componentWillUnmount lifecyle
  // * Runs on the first render because second arg is empty array
  // * returns unmount fn
  useEffect(() => {
    const dispose = () => {
      rendererRef.current?.dispose();
      rendererRef.current = null;
      abortControllerRef.current?.abort();
      viewerDataService.clearPendingCache();
      viewerDataService.clearCloneableGeometries();
      viewerDataService.clearFetchTess();
      viewerDataService.clearPMICache();
      setTargetNode(null);
      //Note: VectorEditor does not provide receiveHasPMIAnnotations
      if (receiveHasPMIAnnotations) {
        receiveHasPMIAnnotations({ hasPMIAnnotations: false }); //reset
      }
      window.removeEventListener('beforeunload', dispose);
    };
    window.addEventListener('beforeunload', dispose);
    return dispose;
  }, [receiveHasPMIAnnotations]);

  // implement collecting cumulative selection information from both historical and current selections
  // TODO: elevate this to viewer component once its been converted to hooks
  useEffect(() => {
    if (onNewSelectorCumulativeInfo) {
      const cumulativeInfo: { area: number; length: number } = {
        area: 0,
        length: 0,
      };
      for (const selectedObj of chain(
        historicalSelectedObjects,
        selectedObjects,
      )) {
        switch (selectedObj.type) {
          case ENTITY_TYPES.FACE:
            cumulativeInfo.area +=
              (selectedObj.obj as StepFaceModel)?.metaData.area || 0;
            break;
          case ENTITY_TYPES.EDGE:
            cumulativeInfo.length +=
              (selectedObj.obj as StepEdgeModel)?.metaData.length || 0;
            break;
          case ENTITY_TYPES.STL: {
            const { face } = selectedObj;
            const { vertices } = face as {
              vertices: [Vector3, Vector3, Vector3];
            };
            const ab = new Vector3().subVectors(vertices[1], vertices[0]);
            const ac = new Vector3().subVectors(vertices[2], vertices[0]);
            const bc = new Vector3().subVectors(vertices[2], vertices[1]);
            //Calculate length first because ab.cross(ac) mutates ab
            cumulativeInfo.length += ab.length() + ac.length() + bc.length();
            const area = 0.5 * ab.cross(ac).length();
            cumulativeInfo.area += area;
            break;
          }
        }
      }
      onNewSelectorCumulativeInfo({ cumulativeInfo });
    }
  }, [historicalSelectedObjects, selectedObjects, onNewSelectorCumulativeInfo]);

  // implement watchers for updating state in the renderer
  useEffect(() => {
    if (rendererRef.current && !loadingProgress.isLoadingVisible) {
      rendererRef.current.updateState(rendererStateRef.current);
    }
  }, [
    //showHoverObject, //TODO: By adding showHoverObject, renderer.state will be updated
    //visibleBodies,
    activeBodies,
    activeCaliperTool,
    activeSelectorTool,
    caliperObjectsKeys,
    caliperToolSelections,
    cameraPosition,
    cameraZoom,
    controlTarget,
    dimensionalPrecision,
    displayScale,
    displayUnits,
    editorMode,
    enableSelectorTool,
    explodedState,
    featureSnapTo,
    focusedBodies,
    forceFocus,
    forceView,
    highlight,
    initializeVectorStyleByNameMap,
    internalHighlight,
    isDxfFinalizing,
    isFocused,
    isFocusedViewActive,
    isPaused,
    isTweening,
    loadingProgress,
    materialStockResults,
    measurement,
    nativeUnits,
    normals,
    onlyShowHighlighted,
    orthoView,
    pmiUrlByPartFileUuid,
    receiveSerializedDXF,
    renderNativeColors,
    selectedBodies,
    selectedObjects,
    selectedObjectsKeys,
    selectorToolSelections,
    showAdditionalKeys,
    showAdditionalKeysExplicit,
    showAxisAlignedBoundingBox,
    showOptimalBoundingBox,
    updatedVectors,
    updatedVectorsCounter,
    visibleStockResultKeys,
    wireframe,
  ]);

  //When pendingPartFileUuids or renderer changes
  //* accumulate newPendingPartFileUuids and call loadPartData
  //* IMPORTANT: Note that loadPartData is async, so it is possible for this useEffect to be called while
  //  a previous loadPartData is still running.
  //  * This is why it is important to calculate newPendingPartFileUuids
  //  * It is also important to use abortController.signal because the renderer may be in process of being disposed
  useEffect(() => {
    if (
      !targetNode ||
      !bodies ||
      !assemblyInstances ||
      !bodyIdsByPartFileUuid ||
      !bodyOrAssemblyInstanceById ||
      !partFileUuidsWithPMIRef.current ||
      !rendererRef.current ||
      rendererRef.current.disposed
    ) {
      return;
    }
    const newPendingPartFileUuids = new Set<string>();
    //Note: _pendingPartFileUuids changes each time onLoadPartFile() is called
    //so tracking the pending here in pendingPartFileUuids Set.
    for (const pendingPartFileUuid of _pendingPartFileUuids) {
      if (
        !pendingPartFileUuidsRef.current.has(pendingPartFileUuid) &&
        !partOrAssemblyById.has(pendingPartFileUuid)
      ) {
        pendingPartFileUuidsRef.current.add(pendingPartFileUuid);
        newPendingPartFileUuids.add(pendingPartFileUuid);
      }
    }
    if (newPendingPartFileUuids.size > 0) {
      rendererRef.current
        .loadPartDatas(newPendingPartFileUuids, rendererStateRef.current)
        .catch((err) => {
          //Handled by logging. Note the loadingProgress controller will also notify user via its processSignal
          console.error(err);
          return Promise.resolve();
        });
    }
  }, [_pendingPartFileUuids, targetNode, bodies, assemblyInstances, bodyIdsByPartFileUuid, bodyOrAssemblyInstanceById, partOrAssemblyById]);

  useEffect(() => {
    if (
      !rendererRef.current ||
      !targetNode ||
      !bodies ||
      !assemblyInstances ||
      !bodyIdsByPartFileUuid ||
      !bodyOrAssemblyInstanceById ||
      !partFileUuidsWithPMIRef.current ||
      !visibleBodies
    ) {
      return;
    }

    //Warning: DO NOT test areArraysAsSetDifferent(visibleBodies, renderer.state.visibleBodies)
    //If the user is rapidly changing visibleBodies, it can lead to skipping loading a body
    //The priority queue can deal with rapidly changing visibleBodies and will ensure the last
    //change to visibleBodies will call renderer.updateState()
    rendererRef.current?.loadBodies(rendererStateRef.current).catch((err) => {
      console.error(err); //Handled by logging.
      return Promise.resolve();
    });
  }, [visibleBodies, targetNode, bodies, assemblyInstances, bodyIdsByPartFileUuid, bodyOrAssemblyInstanceById]);

  const handleTargetNodeRef = useCallback((node: HTMLDivElement) => {
    setTargetNode(node);
  }, []);

  return <RendererDiv {...props} ref={handleTargetNodeRef} />;
});

export const rendererDefaultProps: Partial<RenderComponentProps> = {
  activeBodies: [],
  focusedBodies: [],
  visibleBodies: [],
  visibleStockResultKeys: [],
  explodedState: {},
  showAxisAlignedBoundingBox: false,
  showOptimalBoundingBox: false,
  wireframe: false,
  renderNativeColors: false,
  onlyShowHighlighted: false,
  isFocusedViewActive: false,
  highlight: [],
  internalHighlight: [],
  normals: [],
  showAdditionalKeys: [],
  showAdditionalKeysExplicit: [],
  measurement: null,
  cameraPosition: new Vector3(),
  bodyPoseInformation: {},
  displayScale: 1.0,
  selectedObjects: [],
  selectedObjectsKeys: [],
  selectedBodies: [],
  updatedVectors: [],
  selectorToolSelections: [],
  caliperToolSelections: [],
  caliperObjectsKeys: [],
  nativeUnits: UNITS.MM,
  displayUnits: UNITS.IN,
  dimensionalPrecision: 3,
  env: ENVIRONMENT_TYPES.PROD,
  materialStockResults: [],
  viewerMode: PART_VIEWER_MODES.FULL,
};

export default RendererComponent;
